import {getTestProps} from '@benchling/js-test-utils';
import {createStyles} from '@mantine/core';
import React from 'react';

import * as benchlingUtil from 'BenchlingUtil';
import {agentHomepageUrl} from 'llm/routes/UrlUtils';
import APIUtil from 'util/APIUtil';

export const BENCHLING_AI_CLIENT_ID = 'benchling-ai';
export const BENCHLING_AI_CLIENT_NAME = 'Benchling AI';
export const BENCHLING_AI_SUB_CLIENT_ID_PREFIX = 'benchling-ai/';
export const BENCHLING_AI_HELP_URL =
  'https://help.benchling.com/hc/en-us/articles/45766024411405-Use-Benchling-AI-chat';
export const AGENT_ATTRIBUTION_REGEX = /^benchling-ai(?:\/(.+))?$/;

const useStyles = createStyles((theme) => ({
  attributionLink: {
    color: theme.colors.gray[5],
  },
}));

interface AgentAttributionLinkProps {
  clientName: string;
  agentId: string;
  userId: string;
}

function AttributionLink(props: React.ComponentPropsWithoutRef<'a'>): JSX.Element {
  const {classes} = useStyles();
  return <a className={classes.attributionLink} {...props} />;
}

function BenchlingAIAttributionLink({clientName}: {clientName: string}): JSX.Element {
  return (
    <AttributionLink
      {...getTestProps({component: 'AgentAttributionLink', element: 'benchlingAiLink'})}
      href={BENCHLING_AI_HELP_URL}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(event) => {
        event.stopPropagation();
      }}
    >
      {clientName}
    </AttributionLink>
  );
}

function AgentAttributionLink({clientName, agentId, userId}: AgentAttributionLinkProps): JSX.Element {
  const agentUrl = agentHomepageUrl(agentId);
  const attributionName = userId === agentId ? BENCHLING_AI_CLIENT_NAME : clientName;

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>): void => {
    event.stopPropagation();
    benchlingUtil.navigate(event);
  };

  return (
    <AttributionLink
      {...getTestProps({component: 'AgentAttributionLink', element: 'agentLink'})}
      href={agentUrl}
      onClick={handleClick}
    >
      {attributionName}
    </AttributionLink>
  );
}

interface AgentAttribution {
  clientId: string;
  clientName: string;
  userId: string;
}

export function resolveAgentAttribution({
  clientId,
  clientName,
  userId,
}: AgentAttribution): React.ReactNode | undefined {
  const match = AGENT_ATTRIBUTION_REGEX.exec(clientId);
  if (match == null) {
    return undefined;
  }

  const agentId = match[1];

  if (agentId != null && APIUtil.isRoutine(agentId)) {
    return <AgentAttributionLink clientName={clientName} agentId={agentId} userId={userId} />;
  }

  return <BenchlingAIAttributionLink clientName={clientName} />;
}
